import React, { useState, useEffect } from 'react';
import { Plus, Play, Pause, Archive, Edit, Eye, BarChart3, X, Target, Image, Palette, Video, Trash2 } from 'lucide-react';
import { adsApiService } from '../services/adsApiService';

const CampaignsView: React.FC = () => {
  const SAC_PER_30S_BLOCK = 9;
  const MAX_BLOCKS_PER_24H_PER_USER = 240;
  const MAX_CAMPAIGNS_PER_USER = 100;
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [creatives, setCreatives] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    objective: '',
    budget_amount: 0, // in SAC
    status: 'paused',
    start_date: '',
    end_date: ''
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    objective: 'brand_awareness',
    budget_type: 'daily',
    budget_amount: 90, // Default to 90 SAC
    start_date: '',
    end_date: '',
    selected_creatives: [] as string[],
  });

  const formatSAC = (amount: number) => {
    return `${amount} SAC`;
  };

  useEffect(() => {
    loadCampaigns();
    loadAccounts();
    loadCreatives();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await adsApiService.getAdAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadCreatives = async () => {
    try {
      const data = await adsApiService.getCreatives();
      setCreatives(data);
    } catch (err) {
      console.error('Error loading creatives:', err);
    }
  };
  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await adsApiService.getCampaigns();
      setCampaigns(data);
      console.log('Existing campaigns:', data); // Log campaigns to console
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const toDateTimeLocalValue = (value: string | null | undefined) => {
    if (!value) return '';
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const toIsoOrEmpty = (value: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
  };

  const blocksFromSac = (sac: number) => {
    const blocks = sac / SAC_PER_30S_BLOCK;
    return Number.isFinite(blocks) ? Math.round(blocks) : 0;
  };

  const sacFromBlocks = (blocks: number) => {
    const safeBlocks = Number.isFinite(blocks) ? Math.max(0, Math.floor(blocks)) : 0;
    return safeBlocks * SAC_PER_30S_BLOCK;
  };

  const formatTimeFromBlocks = (blocks: number) => {
    const seconds = Math.max(0, blocks) * 30;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds}s`;
    if (remainingSeconds === 0) return `${minutes} min`;
    return `${minutes} min ${remainingSeconds}s`;
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    if (accounts.length === 0) return;

    try {
      if (campaigns.length >= MAX_CAMPAIGNS_PER_USER) {
        throw new Error(`Has alcanzado el límite de ${MAX_CAMPAIGNS_PER_USER} campañas por usuario.`);
      }

      const startIso = toIsoOrEmpty(createForm.start_date);
      const endIso = toIsoOrEmpty(createForm.end_date);

      if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
        throw new Error('La fecha/hora de fin debe ser posterior a la fecha/hora de inicio.');
      }

      const blocksPerDay = blocksFromSac(createForm.budget_amount);
      if (blocksPerDay <= 0) {
        throw new Error('Selecciona al menos 1 bloque de 30 segundos por día.');
      }
      if (blocksPerDay > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`El máximo por usuario es ${MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24 horas (2 horas).`);
      }

      const activeBlocks = campaigns
        .filter((c) => c.status === 'active')
        .reduce((sum, c) => sum + blocksFromSac(c.budget_amount || 0), 0);
      if (activeBlocks + blocksPerDay > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`Superas el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - activeBlocks)} bloques disponibles.`);
      }

      const newCampaign = await adsApiService.createCampaign({
        ad_account_id: accounts[0].id,
        ...createForm,
        start_date: startIso || '',
        end_date: endIso || '',
        creatives: createForm.selected_creatives
      });
      
      setCampaigns(prev => [newCampaign, ...prev]);
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        objective: 'brand_awareness',
        budget_type: 'daily',
        budget_amount: 90,
        start_date: '',
        end_date: '',
        selected_creatives: [],
      });
      
      // Success feedback
      console.log('Campaign created successfully:', newCampaign);
      
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Error al crear campaña: ' + (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCampaignAction = async (campaignId: string, action: 'activate' | 'pause' | 'archive') => {
    try {
      let updatedCampaign;
      switch (action) {
        case 'activate':
          {
            const targetCampaign = campaigns.find((c) => c.id === campaignId);
            const targetBlocks = blocksFromSac(targetCampaign?.budget_amount || 0);
            const activeBlocks = campaigns
              .filter((c) => c.status === 'active' && c.id !== campaignId)
              .reduce((sum, c) => sum + blocksFromSac(c.budget_amount || 0), 0);
            if (activeBlocks + targetBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
              alert(`No puedes activar esta campaña: superarías el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - activeBlocks)} bloques.`);
              return;
            }
          }
          updatedCampaign = await adsApiService.activateCampaign(campaignId);
          break;
        case 'pause':
          updatedCampaign = await adsApiService.pauseCampaign(campaignId);
          break;
        case 'archive':
          updatedCampaign = await adsApiService.archiveCampaign(campaignId);
          break;
      }
      
      setCampaigns(prev => prev.map(campaign => 
        campaign.id === campaignId ? updatedCampaign : campaign
      ));
    } catch (err) {
      console.error(`Error ${action}ing campaign:`, err);
      alert(`Error al ${action === 'activate' ? 'activar' : action === 'pause' ? 'pausar' : 'archivar'} campaña`);
    }
  };

  const handleViewCampaign = (campaign: any) => {
    setSelectedCampaign(campaign);
    setShowViewModal(true);
  };

  const handleEditCampaign = (campaign: any) => {
    setSelectedCampaign(campaign);
    setEditForm({
      name: campaign.name,
      objective: campaign.objective,
      budget_amount: campaign.budget_amount,
      status: campaign.status,
      start_date: toDateTimeLocalValue(campaign.start_date),
      end_date: toDateTimeLocalValue(campaign.end_date)
    });
    setShowEditModal(true);
  };

  const handleViewAnalytics = async (campaign: any) => {
    setSelectedCampaign(campaign);
    setAnalyticsLoading(true);
    
    try {
      const analyticsData = await adsApiService.getAnalytics({
        campaign_id: campaign.id,
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
      });
      setCampaignAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading campaign analytics:', err);
      setCampaignAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
    
    setShowAnalyticsModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCampaign) return;
    
    try {
      const startIso = toIsoOrEmpty(editForm.start_date);
      const endIso = toIsoOrEmpty(editForm.end_date);

      if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
        throw new Error('La fecha/hora de fin debe ser posterior a la fecha/hora de inicio.');
      }

      const editBlocks = blocksFromSac(editForm.budget_amount);
      if (editBlocks <= 0) {
        throw new Error('Selecciona al menos 1 bloque de 30 segundos por 24h.');
      }
      if (editBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`El máximo por usuario es ${MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24 horas (2 horas).`);
      }

      const otherActiveBlocks = campaigns
        .filter((c) => c.status === 'active' && c.id !== selectedCampaign.id)
        .reduce((sum, c) => sum + blocksFromSac(c.budget_amount || 0), 0);
      const totalActiveBlocks = otherActiveBlocks + (editForm.status === 'active' ? editBlocks : 0);
      if (totalActiveBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`Superas el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - otherActiveBlocks)} bloques disponibles.`);
      }

      const updates: any = {
        ...editForm,
      };
      updates.budget_amount = sacFromBlocks(editBlocks);

      if (editForm.start_date) {
        updates.start_date = startIso;
      } else {
        delete updates.start_date;
      }

      if (editForm.end_date) {
        updates.end_date = endIso;
      } else {
        updates.end_date = null;
      }

      const updatedCampaign = await adsApiService.updateCampaign(selectedCampaign.id, updates);
      setCampaigns(prev => prev.map(campaign => 
        campaign.id === selectedCampaign.id ? updatedCampaign : campaign
      ));
      setShowEditModal(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error('Error updating campaign:', err);
      alert('Error al actualizar campaña: ' + (err as Error).message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getObjectiveLabel = (objective: string) => {
    const labels: Record<string, string> = {
      brand_awareness: 'Notoriedad de marca',
      reach: 'Alcance',
      traffic: 'Tráfico',
      engagement: 'Interacción',
      conversions: 'Conversiones',
      lead_generation: 'Generación de leads'
    };
    return labels[objective] || objective;
  };

  const toggleCreativeSelection = (creativeId: string) => {
    setCreateForm(prev => ({
      ...prev,
      selected_creatives: prev.selected_creatives.includes(creativeId)
        ? prev.selected_creatives.filter(id => id !== creativeId)
        : [...prev.selected_creatives, creativeId]
    }));
  };

  const handleDeleteCampaign = async (campaign: any) => {
    if (campaign.status === 'active') {
      alert('No puedes eliminar una campaña activa. Paúsala o archívala primero.');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta campaña? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await adsApiService.deleteCampaign(campaign.id);
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
      console.log('Campaign deleted successfully:', campaign.id);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Error al eliminar campaña: ' + (err as Error).message);
    }
  };


  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-24 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      {/* Educational Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 lg:p-6 mb-6 lg:mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 rounded-xl">
            <Target className="text-white" size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">🎯 ¿Qué es una Campaña?</h2>
              <p className="text-gray-700 mb-3 text-sm lg:text-base">
                Una <strong>campaña</strong> es como contratar un vendedor 24/7 que promociona tu arte a miles de personas. 
                Tú decides cuánto gastar y a quién llegar.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-4 text-xs lg:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span><strong>Objetivo:</strong> Más ventas, más seguidores</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span><strong>Consumo:</strong> Bloques de 30s (máx 2h/24h)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span><strong>Control:</strong> Pausa cuando quieras</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Gestión de Campañas</h1>
            <p className="text-gray-600 text-sm lg:text-base">Crea y gestiona tus campañas publicitarias</p>
          </div>
          <button
            data-tour="campaigns-new"
            onClick={() => {
              if (campaigns.length >= MAX_CAMPAIGNS_PER_USER) {
                alert(`Has alcanzado el límite de ${MAX_CAMPAIGNS_PER_USER} campañas por usuario.`);
                return;
              }
              setShowCreateModal(true);
            }}
            disabled={campaigns.length >= MAX_CAMPAIGNS_PER_USER}
            className={`flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-3 rounded-xl transition-all shadow-lg text-sm lg:text-base whitespace-nowrap ${
              campaigns.length >= MAX_CAMPAIGNS_PER_USER
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl transform hover:scale-105'
            }`}
          >
            <Plus size={20} />
            Nueva Campaña
          </button>
        </div>

        {/* Campaigns Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Play className="text-green-600" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl lg:text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.status === 'active').length}
                </p>
                <p className="text-gray-600 text-xs lg:text-sm">Activas</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Pause className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.status === 'paused').length}
                </p>
                <p className="text-gray-600 text-sm">Pausadas</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
                <p className="text-gray-600 text-sm">Total</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Archive className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.status === 'archived').length}
                </p>
                <p className="text-gray-600 text-sm">Archivadas</p>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900">Todas las Campañas</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="p-4 lg:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-900 truncate">{campaign.name}</h3>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                        {campaign.status === 'active' ? 'Activa' : 
                         campaign.status === 'paused' ? 'Pausada' : 'Archivada'}
                      </span>
                    </div>
                    
                    <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-6 text-xs lg:text-sm text-gray-600">
                      <span className="truncate">Objetivo: {getObjectiveLabel(campaign.objective)}</span>
                      <span className="truncate">
                        Tiempo: {formatTimeFromBlocks(blocksFromSac(campaign.budget_amount || 0))}/{campaign.budget_type === 'daily' ? '24h' : 'total'} ({formatSAC(campaign.budget_amount || 0)})
                      </span>
                      <span className="truncate">Creativos: {campaign.ad_sets?.reduce((total: number, adSet: any) => total + (adSet.ads?.length || 0), 0) || 0}</span>
                      {campaign.start_date && (
                        <span className="truncate">Inicio: {new Date(campaign.start_date).toLocaleString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
                    <button 
                      onClick={() => handleViewCampaign(campaign)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Ver detalles de la campaña"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      onClick={() => handleEditCampaign(campaign)}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Editar campaña"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      onClick={() => handleCampaignAction(campaign.id, campaign.status === 'active' ? 'pause' : 'activate')}
                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title={campaign.status === 'active' ? 'Pausar campaña' : 'Activar campaña'}
                    >
                      {campaign.status === 'active' ? <Pause size={18} /> : <Play size={18} />}
                    </button>
                    <button 
                      onClick={() => handleViewAnalytics(campaign)}
                      className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Ver estadísticas de la campaña"
                    >
                      <BarChart3 size={18} />
                    </button>
                    <button 
                      onClick={() => handleDeleteCampaign(campaign)}
                      disabled={campaign.status === 'active'}
                      className={`p-2 rounded-lg transition-colors ${
                        campaign.status === 'active'
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                      }`}
                      title={campaign.status === 'active' ? 'No se puede eliminar una campaña activa' : 'Eliminar campaña'}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {campaigns.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Plus className="text-gray-400" size={32} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay campañas</h3>
              <p className="text-gray-500 mb-6">Crea tu primera campaña para empezar</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                Crear primera campaña
              </button>
            </div>
          )}
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Nueva Campaña</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleCreateCampaign} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {/* Important Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-bold text-blue-900 mb-2">💡 ¿Cómo funciona una campaña?</h3>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>• <strong>Campaña:</strong> El plan general (ej: "Promocionar mis pinturas")</p>
                      <p>• <strong>Creativos:</strong> Los anuncios específicos que verán los usuarios</p>
                      <p>• <strong>Consumo:</strong> Se paga en <strong>bloques de 30 segundos</strong> usando tu saldo de SAC</p>
                      <p>• <strong>Activación:</strong> Una vez creada, aparecerá en la app principal</p>
                    </div>
                  </div>
                  {/* Creatives Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar creativos (anuncios) *
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Elige qué anuncios específicos se mostrarán en esta campaña. Puedes seleccionar varios.
                    </p>
                    
                    {creatives.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                        {creatives.map((creative) => (
                          <div 
                            key={creative.id}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                              createForm.selected_creatives.includes(creative.id)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleCreativeSelection(creative.id)}
                          >
                            <input
                              type="checkbox"
                              checked={createForm.selected_creatives.includes(creative.id)}
                              onChange={() => toggleCreativeSelection(creative.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                              {creative.thumbnail_url ? (
                                <img 
                                  src={creative.thumbnail_url} 
                                  alt={creative.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const nextElement = e.currentTarget.nextElementSibling;
                                    if (nextElement instanceof HTMLElement) {
                                      nextElement.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div className="w-full h-full flex items-center justify-center" style={{display: creative.thumbnail_url ? 'none' : 'flex'}}>
                                {creative.type === 'image' ? (
                                  <Image className="text-gray-500" size={20} />
                                ) : creative.type === 'video' ? (
                                  <Video className="text-gray-500" size={20} />
                                ) : (
                                  <Palette className="text-gray-500" size={20} />
                                )}
                              </div>
                            </div>
                            
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{creative.name}</h4>
                              <p className="text-sm text-gray-600">{creative.title || 'Sin título'}</p>
                              <p className="text-xs text-gray-500">
                                {creative.type} • CTA: {creative.call_to_action} • {new Date(creative.created_at).toLocaleDateString('es-ES')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Palette className="mx-auto text-gray-400 mb-2" size={32} />
                        <p className="text-gray-600 font-medium mb-2">No tienes creativos aún</p>
                        <p className="text-sm text-gray-500 mb-4">
                          Necesitas crear al menos un creativo antes de poder crear una campaña
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateModal(false);
                            setTimeout(() => window.location.hash = '#creatives', 100);
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                        >
                          Crear Mi Primer Creativo
                        </button>
                      </div>
                    )}
                    
                    {createForm.selected_creatives.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                          ✅ <strong>{createForm.selected_creatives.length} creativo{createForm.selected_creatives.length !== 1 ? 's' : ''} seleccionado{createForm.selected_creatives.length !== 1 ? 's' : ''}</strong>
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Estos anuncios se mostrarán rotando automáticamente en la app principal de SoArtistic
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-blue-900 mb-2">⏱️ Cómo funciona el consumo</h4>
                    <div className="text-sm text-blue-900 space-y-1">
                      <p>• Compras <strong>SAC</strong> y la publicidad se consume en <strong>bloques de 30 segundos</strong>.</p>
                      <p>• Precio por bloque: <strong>{SAC_PER_30S_BLOCK} SAC</strong> (equivale a €0,90 si 1 SAC = €0,10).</p>
                      <p>• Máximo por usuario: <strong>{MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24h</strong> (2 horas).</p>
                      <p>• Máximo por usuario: <strong>{MAX_CAMPAIGNS_PER_USER} campañas</strong>.</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de la campaña
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Para tu organización interna. Los usuarios verán el título del creativo.</p>
                    <input
                      type="text"
                      required
                      value={createForm.name}
                      onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: Promoción Arte Digital Enero 2025"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objetivo de la campaña
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Define tu objetivo principal para optimizar el rendimiento</p>
                    <select 
                      value={createForm.objective}
                      onChange={(e) => setCreateForm({...createForm, objective: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="brand_awareness">🎨 Que más gente conozca mi arte</option>
                      <option value="reach">📢 Llegar al máximo de personas</option>
                      <option value="traffic">🌐 Más visitas a mi perfil</option>
                      <option value="engagement">❤️ Más likes y comentarios</option>
                      <option value="conversions">💰 Más ventas y encargos</option>
                      <option value="lead_generation">📞 Conseguir clientes potenciales</option>
                    </select>
                  </div>

                  {/* Campaign + Creatives Explanation */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-bold text-indigo-900 mb-2">🚀 ¿Por qué asociar creativos?</h4>
                    <div className="text-sm text-indigo-800 space-y-1">
                      <p>• <strong>Mayor engagement:</strong> Diferentes anuncios atraen a diferentes personas</p>
                      <p>• <strong>Test A/B automático:</strong> El sistema muestra el creativo que mejor funciona</p>
                      <p>• <strong>No aburrir a la audiencia:</strong> Variedad mantiene el interés</p>
                      <p>• <strong>Optimización continua:</strong> Mejores resultados con múltiples opciones</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de presupuesto
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Recomendamos presupuesto diario para mejor control</p>
                      <select 
                        value={createForm.budget_type}
                        onChange={(e) => setCreateForm({...createForm, budget_type: e.target.value})}
                        disabled
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="daily">🪙 Diario (recomendado)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bloques de 30s por 24h</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Define cuánto tiempo máximo se mostrará tu campaña en un período de 24 horas.
                      </p>
                      <input
                        type="number"
                        min="1"
                        max={MAX_BLOCKS_PER_24H_PER_USER}
                        step="1"
                        required
                        value={Math.max(1, blocksFromSac(createForm.budget_amount))}
                        onChange={(e) => {
                          const blocks = parseInt(e.target.value || '0', 10);
                          setCreateForm({ ...createForm, budget_amount: sacFromBlocks(blocks) });
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        {formatTimeFromBlocks(blocksFromSac(createForm.budget_amount))} / 24h · {formatSAC(createForm.budget_amount)} / 24h
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Inicio (opcional)</label>
                      <p className="text-xs text-gray-500 mb-2">Cuándo empezar a mostrar los anuncios</p>
                      <input
                        type="datetime-local"
                        value={createForm.start_date}
                        onChange={(e) => setCreateForm({...createForm, start_date: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fin (opcional)</label>
                      <p className="text-xs text-gray-500 mb-2">Cuándo parar automáticamente (deja vacío para manual)</p>
                      <input
                        type="datetime-local"
                        value={createForm.end_date}
                        onChange={(e) => setCreateForm({...createForm, end_date: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 mb-2">💳 Ejemplos rápidos</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="font-semibold text-gray-900">1 bloque</p>
                        <p>30 segundos · {formatSAC(SAC_PER_30S_BLOCK)} (≈ €0,90)</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="font-semibold text-gray-900">10 bloques</p>
                        <p>5 minutos · {formatSAC(sacFromBlocks(10))} (≈ €9,00)</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="font-semibold text-gray-900">120 bloques</p>
                        <p>1 hora · {formatSAC(sacFromBlocks(120))} (≈ €108,00)</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="font-semibold text-gray-900">Máximo por 24h</p>
                        <p>240 bloques · 2 horas</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      disabled={submitting}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !createForm.name.trim() || createForm.selected_creatives.length === 0 || !createForm.budget_amount}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {submitting ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Creando...
                        </div>
                      ) : (
                        `🚀 Crear y Activar Campaña${createForm.selected_creatives.length > 0 ? ` (${createForm.selected_creatives.length} anuncio${createForm.selected_creatives.length !== 1 ? 's' : ''})` : ''}`
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Campaign Modal */}
        {showViewModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Detalles de Campaña</h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Nombre</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Estado</label>
                      <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedCampaign.status)}`}>
                        {selectedCampaign.status === 'active' ? 'Activa' : 
                         selectedCampaign.status === 'paused' ? 'Pausada' : 'Archivada'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Objetivo</label>
                      <p className="font-medium text-gray-900">{getObjectiveLabel(selectedCampaign.objective)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Tiempo (24h)</label>
                      <p className="font-medium text-gray-900">
                        {formatTimeFromBlocks(blocksFromSac(selectedCampaign.budget_amount || 0))} ({formatSAC(selectedCampaign.budget_amount || 0)})
                      </p>
                    </div>
                  </div>

                  {selectedCampaign.start_date && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Inicio</label>
                        <p className="font-medium text-gray-900">
                          {new Date(selectedCampaign.start_date).toLocaleString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {selectedCampaign.end_date && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Fin</label>
                          <p className="font-medium text-gray-900">
                            {new Date(selectedCampaign.end_date).toLocaleString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-600">Conjuntos de anuncios</label>
                    <p className="font-medium text-gray-900">
                      {selectedCampaign.ad_sets?.length || 0} conjunto{selectedCampaign.ad_sets?.length !== 1 ? 's' : ''}
                    </p>
                    {selectedCampaign.ad_sets && selectedCampaign.ad_sets.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {selectedCampaign.ad_sets.map((adSet: any) => (
                          <div key={adSet.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex justify-between">
                              <span className="font-medium">{adSet.name}</span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(adSet.status)}`}>
                                {adSet.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {adSet.ads?.length || 0} anuncio{adSet.ads?.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEditCampaign(selectedCampaign);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar Campaña
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleViewAnalytics(selectedCampaign);
                    }}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Ver Estadísticas
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Campaign Modal */}
        {showEditModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Editar Campaña</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>

                <form className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de la campaña
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objetivo
                    </label>
                    <select 
                      value={editForm.objective}
                      onChange={(e) => setEditForm({...editForm, objective: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="brand_awareness">🎨 Notoriedad de marca</option>
                      <option value="reach">📢 Alcance</option>
                      <option value="traffic">🌐 Tráfico</option>
                      <option value="engagement">❤️ Interacción</option>
                      <option value="conversions">💰 Conversiones</option>
                      <option value="lead_generation">📞 Generación de leads</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bloques de 30s por 24h
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={MAX_BLOCKS_PER_24H_PER_USER}
                      step="1"
                      value={Math.max(1, blocksFromSac(editForm.budget_amount))}
                      onChange={(e) => {
                        const blocks = parseInt(e.target.value || '0', 10);
                        setEditForm({ ...editForm, budget_amount: sacFromBlocks(blocks) });
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      {formatTimeFromBlocks(blocksFromSac(editForm.budget_amount))} / 24h · {formatSAC(editForm.budget_amount)} / 24h
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <select 
                      value={editForm.status}
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="active">🟢 Activa</option>
                      <option value="paused">🟡 Pausada</option>
                      <option value="archived">🔒 Archivada</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Inicio (opcional)</label>
                      <input
                        type="datetime-local"
                        value={editForm.start_date}
                        onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fin (opcional)</label>
                      <input
                        type="datetime-local"
                        value={editForm.end_date}
                        onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Guardar Cambios
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {showAnalyticsModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Estadísticas: {selectedCampaign.name}
                  </h2>
                  <button
                    onClick={() => setShowAnalyticsModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>

                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : campaignAnalytics ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-900">
                          {formatNumber(campaignAnalytics.summary.impressions)}
                        </div>
                        <div className="text-sm text-blue-700">Impresiones</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-900">
                          {formatNumber(campaignAnalytics.summary.clicks)}
                        </div>
                        <div className="text-sm text-green-700">Clics</div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-purple-900">
                          {campaignAnalytics.summary.average_ctr.toFixed(2)}%
                        </div>
                        <div className="text-sm text-purple-700">CTR</div>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-orange-900">
                          {formatSAC(campaignAnalytics.summary.spend)}
                        </div>
                        <div className="text-sm text-orange-700">Gastado</div>
                      </div>
                    </div>

                    {/* Performance Table */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Rendimiento por día</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(campaignAnalytics.daily_metrics || campaignAnalytics.metrics).map((metric: any, index: number) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg">
                            <span className="text-sm text-gray-600">
                              {new Date(metric.date).toLocaleDateString('es-ES')}
                            </span>
                            <div className="flex gap-4 text-sm">
                              <span className="text-blue-600">{metric.impressions || 0} imp.</span>
                              <span className="text-green-600">{metric.clicks || 0} clics</span>
                              <span className="text-orange-600">{formatSAC(metric.spend || 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
                    <p className="text-gray-500">Esta campaña aún no tiene métricas registradas</p>
                  </div>
                )}

                <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowAnalyticsModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  export default CampaignsView;
